import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';

import { ContaCaixaConfiguracao } from '../../models/conta-caixa-configuracao';
import { TipoLancamentoConfiguracao } from '../../models/tipo-lancamento-configuracao';
import { AcaoContaMenu } from '../../models/acao-conta-menu';
import { Table } from 'primeng/table';
import { MessageService } from '@services/message.service';
import { ConfiguracaoCaixaApiService } from '../../services/configuracao-caixa-api.service';
import { AdicionarTipoDialogComponent } from '../dialogs/adicionar-tipo-dialog/adicionar-tipo-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { AlterarContaDoTipoDialogComponent } from '../dialogs/alterar-conta-do-tipo-dialog/alterar-conta-do-tipo-dialog.component';
import { MesclarTiposDialogComponent } from '../dialogs/mesclar-tipos-dialog/mesclar-tipos-dialog.component';
import { AlterarContaEmMassaDialogComponent } from '../dialogs/alterar-conta-em-massa-dialog/alterar-conta-em-massa-dialog.component';
import { MesclarTiposEmMassaDialogComponent } from '../dialogs/mesclar-tipos-em-massa-dialog/mesclar-tipos-em-massa-dialog.component';
import { DialogConfirmacaoComponent } from '@components/dialog-confirmacao/dialog-confirmacao.component';
import { PermissionService } from '@services/permission.service';
import { Permissao } from '@models/enums/permissao';

@Component({
  selector: 'app-conta-caixa-table',
  standalone: false,
  templateUrl: './conta-caixa-table.component.html',
  styleUrl: './conta-caixa-table.component.scss'
})
export class ContaCaixaTableComponent {
  @Input() contas: ContaCaixaConfiguracao[] = [];
  @Input() acoesConta: AcaoContaMenu[] = [];
  @Input() mostrarAdicionarTipo: boolean = true;
  @Input() mostrarSelecaoEmMassa: boolean = false;

  @Output() reloadPage = new EventEmitter<void>();

  expandedRows: any = {};

  private contaCaixaClone: { [s: number]: ContaCaixaConfiguracao } = {};
  private tipoLancamentoClone: { [s: number]: TipoLancamentoConfiguracao } = {};

  contaEmModoSelecaoId: number | null = null;
  tiposSelecionados: TipoLancamentoConfiguracao[] = [];

  @ViewChild('dt') table!: Table;

  protected get permissaoEditarTipoLancamento() { return Permissao.EDITAR_TIPO_LANCAMENTO; }

  constructor(
    private configuracaoCaixaApiService: ConfiguracaoCaixaApiService,
    private messageService: MessageService,
    private dialog: MatDialog,
    private permissionService: PermissionService,
  ) { }

  public onRowEditContaInit(conta: ContaCaixaConfiguracao): void {
    this.contaCaixaClone[conta.id] = { ...conta };
  }

  public onRowEditContaSave(conta: ContaCaixaConfiguracao): void {
    this.configuracaoCaixaApiService.saveAlterarNomeContaCaixa(conta).subscribe({
      next: () => {
        this.messageService.showToastSuccess("Salvo com sucesso.");
        delete this.contaCaixaClone[conta.id];
      },
      error: () => this.onRowEditContaCancel(conta)
    });
  }

  public onRowEditContaCancel(conta: ContaCaixaConfiguracao): void {
    const clone = this.contaCaixaClone[conta.id];
    conta.nome = clone.nome;
    delete this.contaCaixaClone[conta.id];
  }

  public onRowEditTipoInit(tipo: TipoLancamentoConfiguracao): void {
    this.tipoLancamentoClone[tipo.id] = { ...tipo };
  }

  public onRowEditTipoSave(tipo: TipoLancamentoConfiguracao): void {
    this.configuracaoCaixaApiService.saveAlterarNomeTipoLancamento(tipo).subscribe({
      next: () => {
        this.messageService.showToastSuccess("Salvo com sucesso.");
        delete this.tipoLancamentoClone[tipo.id];
      },
      error: () => this.onRowEditTipoCancel(tipo)
    });
  }

  public onRowEditTipoCancel(tipo: TipoLancamentoConfiguracao): void {
    const clone = this.tipoLancamentoClone[tipo.id];
    tipo.nome = clone.nome;
    delete this.tipoLancamentoClone[tipo.id];
  }

  public acaoContaVisivel(acao: AcaoContaMenu, conta: ContaCaixaConfiguracao): boolean {
    if (acao.permissao && !this.permissionService.has(acao.permissao)) return false;
    return acao.visivel ? acao.visivel(conta) : true;
  }

  public filtrar(valor: string) {
    this.table.filterGlobal(valor, 'contains');
  }

  public estaEmModoSelecao(conta: ContaCaixaConfiguracao): boolean {
    return this.contaEmModoSelecaoId === conta.id;
  }

  public get qtdSelecionados(): number {
    return this.tiposSelecionados.length;
  }

  public ativarModoSelecao(conta: ContaCaixaConfiguracao): void {
    if (this.contaEmModoSelecaoId !== null && this.contaEmModoSelecaoId !== conta.id) {
      const contaAnterior = this.contas.find(c => c.id === this.contaEmModoSelecaoId)!;
      const dialogRef = this.dialog.open(DialogConfirmacaoComponent, {
        width: '467px',
        panelClass: 'no-padding-dialog',
        disableClose: true
      });
      dialogRef.componentInstance.titulo = 'Seleção em andamento';
      dialogRef.componentInstance.mensagem = `A conta "${contaAnterior.nome}" já está em modo de seleção. Ao continuar, a seleção atual será cancelada.`;
      dialogRef.componentInstance.textoBtnConfirmacao = 'Continuar';
      dialogRef.componentInstance.acaoConfirmacao = () => {
        this.cancelarModoSelecao();
        this.iniciarModoSelecao(conta);
      };
      return;
    }

    this.iniciarModoSelecao(conta);
  }

  private iniciarModoSelecao(conta: ContaCaixaConfiguracao): void {
    this.tiposSelecionados = [];
    this.contaEmModoSelecaoId = conta.id;
    this.expandedRows = { ...this.expandedRows, [conta.id]: true };
  }

  public cancelarModoSelecao(): void {
    this.contaEmModoSelecaoId = null;
    this.tiposSelecionados = [];
  }

  public adicionarTipoClick(conta: ContaCaixaConfiguracao): void {
    const dialogRef = this.dialog.open(AdicionarTipoDialogComponent, {
      width: '720px',
      panelClass: 'no-padding-dialog',
      disableClose: true
    });
    dialogRef.componentInstance.contaId = conta.id;
    dialogRef.afterClosed().subscribe((salvo: boolean | undefined) => {
      if (salvo)
        this.reloadPage.emit();
    });
  }

  public mesclarTiposClick(tipo: TipoLancamentoConfiguracao, conta: ContaCaixaConfiguracao): void {
    const dialogRef = this.dialog.open(MesclarTiposDialogComponent, {
      width: '720px',
      panelClass: 'no-padding-dialog',
      disableClose: true
    });
    dialogRef.componentInstance.tipoAtual = tipo;
    dialogRef.componentInstance.tipos = conta.tipos.filter(t => t.id !== tipo.id);
    dialogRef.afterClosed().subscribe((salvo: boolean | undefined) => {
      if (salvo)
        this.reloadPage.emit();
    });
  }

  public alterarContaTipoClick(tipo: TipoLancamentoConfiguracao, conta: ContaCaixaConfiguracao): void {
    const dialogRef = this.dialog.open(AlterarContaDoTipoDialogComponent, {
      width: '720px',
      panelClass: 'no-padding-dialog',
      disableClose: true
    });
    dialogRef.componentInstance.contas = this.contas;
    dialogRef.componentInstance.tipoAtual = tipo;
    dialogRef.componentInstance.contaDoTipo = conta;
    dialogRef.afterClosed().subscribe((salvo: boolean | undefined) => {
      if (salvo)
        this.reloadPage.emit();
    });
  }

  public alterarContaEmMassaClick(conta: ContaCaixaConfiguracao): void {
    if (this.qtdSelecionados === 0) {
      this.messageService.showToastWarning('Selecione pelo menos 1 tipo');
      return;
    }

    const dialogRef = this.dialog.open(AlterarContaEmMassaDialogComponent, {
      width: '720px',
      panelClass: 'no-padding-dialog',
      disableClose: true
    });
    dialogRef.componentInstance.tipos = this.tiposSelecionados;
    dialogRef.componentInstance.contas = this.contas;
    dialogRef.componentInstance.contaAtual = conta;
    dialogRef.afterClosed().subscribe((salvo: boolean | undefined) => {
      if (salvo) {
        this.cancelarModoSelecao();
        this.reloadPage.emit();
      }
    });
  }

  public mesclarTiposEmMassaClick(conta: ContaCaixaConfiguracao): void {
    if (this.qtdSelecionados < 2) {
      this.messageService.showToastWarning('Selecione pelo menos 2 tipos para mesclar');
      return;
    }

    const dialogRef = this.dialog.open(MesclarTiposEmMassaDialogComponent, {
      width: '720px',
      panelClass: 'no-padding-dialog',
      disableClose: true
    });
    dialogRef.componentInstance.tipos = this.tiposSelecionados;
    dialogRef.afterClosed().subscribe((salvo: boolean | undefined) => {
      if (salvo) {
        this.cancelarModoSelecao();
        this.reloadPage.emit();
      }
    });
  }
}
